import { useFetch } from "../../hooks/useFetch";
import api, { money, formatApiError } from "../../lib/api";
import { Loading, EmptyState } from "../../components/States";
import { CHead, CCard, Pill } from "../../components/customer/ui";
import { toast } from "sonner";
import { PaymentModeBadge } from '../../components/PaymentModeBadge';
import { CreditCard, CheckCircle2, Receipt } from "lucide-react";

export default function CustomerPayments() {
  const invoices = useFetch("/invoices");
  const payments = useFetch("/payments");
  if (invoices.loading || payments.loading) return <Loading />;

  const pay = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
  };

  const invs = invoices.data || [];
  const due = invs.filter((i) => ["sent", "overdue"].includes(i.status));
  const balance = due.reduce((a, i) => a + i.amount, 0);

  return (
    <div className="space-y-6">
      <PaymentModeBadge />
      <CHead title="Payments" subtitle="View balances, pay invoices, and see your history." />

      <CCard className="p-6 text-center">
        <div className="text-xs font-bold uppercase tracking-widest text-[#1286F2]">Balance due</div>
        <div className="mt-2 text-5xl font-black text-slate-900">{money(balance)}</div>
      </CCard>

      <div className="space-y-3">
        <div className="text-sm font-bold uppercase tracking-wide text-slate-500">Invoices</div>
        {due.length === 0 && <EmptyState title="You're all paid up" subtitle="No outstanding balance." icon={CheckCircle2} />}
        {due.map((i) => (
          <CCard key={i.id} className="p-5" data-testid={`c-invoice-${i.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{i.description}</div>
                <div className="text-sm text-slate-400">{i.number}</div>
              </div>
              <Pill status={i.status} />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-2xl font-bold text-[#1286F2]">{money(i.amount)}</span>
              <button onClick={() => pay(i.id)} className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-6 py-3 font-bold text-white hover:bg-[#0A84FF]" data-testid={`pay-invoice-${i.id}`}>
                <CreditCard size={18} /> Pay Now
              </button>
            </div>
          </CCard>
        ))}
      </div>

      {(payments.data || []).length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-slate-500"><Receipt size={14} /> Payment history</div>
          {payments.data.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-4" data-testid={`c-payment-${p.id}`}>
              <div>
                <div className="font-medium text-slate-900">{p.invoice_number} · {p.method}</div>
                <div className="text-sm text-slate-400">{new Date(p.date).toLocaleDateString()}</div>
              </div>
              <span className="font-semibold text-emerald-600">{money(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
